import React, { useState } from "react";
import { usePageMeta } from "../lib/usePageMeta";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PaperPlaneRight, EnvelopeSimple, Buildings, Phone } from "@phosphor-icons/react";

export default function Contact() {
  usePageMeta({
    title: "Contact · Support & Sales",
    description: "Contact the Medical Peptide Store team. Customer support and sales. Email info@medicalpeptidestore.com or call (888) 294-7717.",
    canonicalPath: "/contact",
    jsonLd: [{
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact — Medical Peptide Store",
      "url": "https://medicalpeptidestore.com/contact",
      "contactPoint": [{
        "@type": "ContactPoint",
        "email": "info@medicalpeptidestore.com",
        "telephone": "+1-888-294-7717",
        "contactType": "customer support",
        "areaServed": "US",
        "availableLanguage": "en"
      }]
    }],
  });
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-14 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="contact-page" data-clarity-mask="true">
      <div className="lg:col-span-5">
        <div className="overline mb-3">Contact</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">
          Get in touch
        </h1>
        <p className="mt-4 text-brand-muted text-sm max-w-md leading-relaxed">
          For account applications, product availability, or documentation inquiries. We respond within one business day.
        </p>
        <div className="mt-10 space-y-4 text-sm text-brand-muted">
          <div className="flex items-start gap-3">
            <EnvelopeSimple size={18} weight="duotone" className="text-brand-accent mt-0.5" />
            <div>
              <a href="mailto:info@medicalpeptidestore.com" data-testid="contact-email-address" className="text-brand-navy font-mono hover:text-brand-accent">
                info@medicalpeptidestore.com
              </a>
              <div className="text-xs text-brand-muted">General inquiries, orders, and support</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone size={18} weight="duotone" className="text-brand-accent mt-0.5" />
            <div>
              <a href="tel:+18882947717" data-testid="contact-phone" className="text-brand-navy font-mono hover:text-brand-accent">
                (888) 294-7717
              </a>
              <div className="text-xs text-brand-muted">Mon–Fri, 9am–6pm ET</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Buildings size={18} weight="duotone" className="text-brand-accent mt-0.5" />
            <div>
              <div className="text-brand-navy font-mono">Customer support</div>
              <div className="text-xs text-brand-muted">Typical response within one business day</div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        <form onSubmit={submit} className="border border-brand-border bg-white p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Your name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            </div>
          </div>
          <div>
            <Label>Subject</Label>
            <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="contact-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-6 py-3 text-sm tracking-wide transition-colors disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send message"} <PaperPlaneRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
